import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { allorders, createOrder, retryPayment, verifyPayment } from "../Redux/Slice/Payment.slice";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StripeCheckoutForm from "../component/StripeCheckoutForm";
import OrderSuccessModal from "../component/OrderSuccessModal";
import axiosInstance from "../Utils/axiosInstance";
import { Modal } from "@mui/material";
import { motion } from "framer-motion";
import { decryptData } from "../Utils/encryption";
import { useNavigate } from "react-router-dom";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { FaDownload } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useSnackbar } from "notistack";
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Orders = ({ viewMode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Orders • YOYO";
    window.scrollTo(0, 0);
  }, []);

  const allOrders = useSelector((state) => state.payment.orders)

  const user = useSelector((state) => state.auth.user);
  const [showMoreFor, setShowMoreFor] = useState(null);
  // const [viewMode, setViewMode] = useState("card"); // 'card' or 'list'
  const [showAllItemsForOrder, setShowAllItemsForOrder] = useState(null); // New state to control showing all items for a specific order
  const [processingOrderId, setProcessingOrderId] = useState(null)
  const [invoiceModal, setInvoiceModal] = useState({ open: false, order: null });
  const [openStripeModal, setOpenStripeModal] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Helper to format date
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewInvoice = (order) => {
    setInvoiceModal({ open: true, order: order });
  };

  const closeInvoiceModal = () => {
    setInvoiceModal({ open: false, order: null });
  };

  const downloadFromModal = async () => {
    if (invoiceModal.order) {
      try {
        const userDetails = {
          name: user?.fullName ? decryptData(user.fullName) : "Customer",
          email: user?.email ? decryptData(user.email) : "",
        };
        await generateInvoice(invoiceModal.order, userDetails, 'save');
      } catch (error) {
        console.error("Error generating invoice for download:", error);
      }
    }
  };

  const fadeUp = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
    },
  };

  // Helper functions for directional hover effect
  const getDirection = (e, element) => {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;

    const top = y;
    const right = w - x;
    const bottom = h - y;
    const left = x;

    const min = Math.min(top, right, bottom, left);

    if (min === top) return "top";
    if (min === right) return "right";
    if (min === bottom) return "bottom";
    return "left";
  };

  const mapDirToTransform = (dir) => {
    const map = {
      top: "translate3d(0, -100%, 0)",
      right: "translate3d(100%, 0, 0)",
      bottom: "translate3d(0, 100%, 0)",
      left: "translate3d(-100%, 0, 0)",
    };
    return map[dir];
  };

  // HoverCard component
  function HoverCard({
    className = "",
    children,
    roundedClass = "rounded-xl",
  }) {
    const cardRef = useRef(null);
    const isLeavingRef = useRef(false);
    const enterRafRef = useRef(0);
    const [overlayTransform, setOverlayTransform] = useState(
      "translate3d(0, 0, 0)"
    );
    const [overlayOpacity, setOverlayOpacity] = useState(0);

    const handleEnter = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = false;
      setOverlayOpacity(1);
      setOverlayTransform(mapDirToTransform(dir));
      if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      enterRafRef.current = requestAnimationFrame(() => {
        setOverlayTransform("translate3d(0, 0, 0)");
        enterRafRef.current = 0;
      });
    };

    const handleLeave = (e) => {
      const dir = getDirection(e, cardRef.current);
      isLeavingRef.current = true;
      if (enterRafRef.current) {
        cancelAnimationFrame(enterRafRef.current);
        enterRafRef.current = 0;
      }
      setOverlayOpacity(0);
      setOverlayTransform(mapDirToTransform(dir));
    };

    const handleTransitionEnd = () => {
      if (isLeavingRef.current) {
        setOverlayOpacity(0);
        isLeavingRef.current = false;
      }
    };

    useEffect(() => {
      return () => {
        if (enterRafRef.current) cancelAnimationFrame(enterRafRef.current);
      };
    }, []);

    return (
      <div
        ref={cardRef}
        onPointerEnter={handleEnter}
        onPointerLeave={handleLeave}
        className={`relative overflow-hidden ${roundedClass} ${className}`}
      >
        <div
          className={`pointer-events-none absolute inset-0 ${roundedClass} bg-white/10 border border-transparent transition-all duration-300 ease-out`}
          style={{
            transform: overlayTransform,
            opacity: overlayOpacity,
            willChange: "transform, opacity",
            zIndex: 1,
          }}
          onTransitionEnd={handleTransitionEnd}
        />
        {children}
      </div>
    );
  }

  const handleRetryPayment = async (order) => {
    setProcessingOrderId(order._id)
    if (process.env.REACT_APP_PAYMENT_TYPE === "razorpay") {
      const resultAction = await dispatch(createOrder({ amount: order?.amount }));
      if (createOrder.fulfilled.match(resultAction)) {
        const { razorpayOrder } = resultAction.payload;
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Yoyo",
          description: "Game Purchase",
          order_id: razorpayOrder.id,
          image: "/logo556.png",
          handler: function (response) {
            console.log(response, "response");

            dispatch(verifyPayment({
              items: order.items,
              amount: order.amount,
              paymentResponse: response,
              buyNow: false,
              orderId: order._id
            })).then((res) => {
              if (verifyPayment.fulfilled.match(res)) {
                enqueueSnackbar("Payment successful!", { variant: "success" });
                try {
                  const updatedOrder = res.payload.order;
                  const userDetails = {
                    name: user?.fullName ? decryptData(user.fullName) : "Customer",
                    email: user?.email ? decryptData(user.email) : "",
                  };
                  generateInvoice(updatedOrder, userDetails, "save");
                  setShowSuccessModal(true);
                } catch (e) {
                  console.error("Error auto-opening invoice", e);
                }
              }
            });
          },
          prefill: {
            name: decryptData(user.fullName),
            email: decryptData(user.email),
          },
          theme: {
            color: "#7c3aed",
          },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        setProcessingOrderId(null)
      }
    } else {
      // Stripe flow
      try {
        const response = await axiosInstance.post("/order/stripe/create-payment-intent", {
          amount: order?.amount,
        });

        if (response.data.success) {
          setClientSecret(response.data.clientSecret);
          setOrderData({ items: order.items, amount: order?.amount, orderId: order._id });
          setOpenStripeModal(true);
        }
      } catch (err) {
        console.error("Stripe retry error:", err);
      } finally {
        setProcessingOrderId(null);
      }
    }
  };

  useEffect(() => {
    dispatch(allorders());
  }, []);

  if (!allOrders || allOrders.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
        <div className="text-2xl md:text-3xl font-semibold text-white mb-2">
          You have no orders yet
        </div>
        <div className="text-white/60 mb-6">
          Explore our collection and add some games to your cart.
        </div>
        <button
          onClick={() => navigate("/games")}
          className="px-6 py-3 rounded-md bg-gradient-primary transition-all"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  // console.log(allOrders,"allOrders");


  return (
    <>
      <div className="bg-[#1414145d] backdrop-blur-md border-[2px] border-white/15 text-white p-2 md:p-4 lg:p-4 rounded-lg relative ">
        {/* Conditional Rendering */}
        {viewMode === "card" ? (
          <motion.section
            variants={fadeUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="grid xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1 md600:grid-cols-2 grid-cols-1 gap-5"
          >
            {allOrders.map((order, i) => {
              return (
                <motion.div
                  key={order._id}
                  variants={fadeUp}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                >
                  <HoverCard className="group border border-white/10 py-4 px-6 transition duration-300 ease-out hover:-translate-y-1 hover:shadow-lg backdrop-blur-sm h-64 flex flex-col justify-between">
                    {/* Order Header */}
                    <div className="flex justify-between">
                      <div className="text-2xl transition-transform duration-300 group-hover:scale-110">
                        <div
                          className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold ${order.status === "paid"
                            ? "bg-emerald-300/20 text-emerald-300"
                            : "bg-yellow-300/20 text-yellow-300"
                            }`}
                        >
                          {order.status}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <div className="text-white font-semibold text-lg">
                          ${order?.amount?.toFixed(2)}
                        </div>
                        {order.status === "paid" && (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInvoice(order);
                              }}
                              className="flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200 transition-colors"
                              title="View Invoice"
                            >
                              <FaDownload /> Invoice
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/profile/downloads');
                              }}
                              className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                              title="Go to Downloads"
                            >
                              <FaDownload /> Download
                            </button>
                          </div>
                        )}
                        <div className="text-gray-400 text-xs">
                          {order.currency}
                        </div>
                      </div>
                    </div>

                    {/* Order Info */}
                    <div className="mb-4">
                      <h3 className="text-white font-semibold">
                        Order #{order._id.slice(-8)}
                      </h3>
                      <p className="text-gray-400 text-sm mt-1">
                        {formatDate(order.createdAt)}
                      </p>
                    </div>

                    {/* Items Preview */}
                    <div className="space-y-3 mb-4 max-h-20 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-primary/80 scrollbar-track-transparent">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center">
                          <img
                            src={item?.game?.cover_image?.url}
                            alt={item.game?.title}
                            className="w-10 h-10 object-cover rounded-lg border border-white/10"
                          />
                          <div className="ml-1 md600:ml-3 flex-1 min-w-0">
                            <div className="font-medium text-white text-sm truncate">
                              {item.game.title}
                            </div>
                            <div className="text-xs text-gray-400 capitalize">
                              {item.platform}
                            </div>
                          </div>
                          <div className="font-semibold text-purple-300 text-xs md600:text-sm">
                            ${item.price}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Razorpay Order ID */}
                    {order.razorpayOrderId ? (
                      <div className="border-t border-white/10 pt-3">
                        {order.status === "paid" && (
                          <p className="text-gray-400 text-sm py-1">
                            <span className="font-semibold text-gray-300 tracking-wide">
                              Razorpay Order:
                            </span>{" "}
                            {order.razorpayOrderId}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="border-t border-white/10 pt-3">
                        {order.status === "paid" && (
                          <p className="text-gray-400 text-sm py-1">
                            <span className="font-semibold text-gray-300 tracking-wide">
                              Payment ID:
                            </span>{" "}
                            {order.stripePaymentIntentId}
                          </p>
                        )}
                      </div>
                    )}
                    {order.status === "failed" && (
                      <button
                        className="px-4 text-xs  bg-gradient-primary text-white rounded"
                        onClick={() => handleRetryPayment(order)}
                        disabled={processingOrderId === order._id}
                      >
                        {processingOrderId === order._id ? "Processing.." : "Retry Payment"}
                      </button>
                    )}
                    {(order.status === "pending" || order.status === "created") && (
                      <button
                        className=" px-4 text-sm py-1 bg-gradient-primary text-white rounded"
                        onClick={() => handleRetryPayment(order)}
                        disabled={processingOrderId === order._id}
                      >
                        {processingOrderId === order._id ? "Processing.." : "Pay Now"}
                      </button>
                    )}
                  </HoverCard>
                </motion.div>
              );
            })}
          </motion.section>
        ) : (
          // List view
          <div className="overflow-x-auto rounded-lg shadow-lg backdrop-blur-md border border-white/10 h-full scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
            <table className="min-w-[900px] w-full divide-y divide-white/10">
              <thead>
                <tr className="bg-[#2A292A] sticky top-0 z-10">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Razorpay Order ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-200 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {allOrders.map((order, idx) => (
                  <motion.tr
                    key={order._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`transition hover:bg-purple-900/20 group ${idx % 2 === 0 ? "bg-white/5" : "bg-white/0"
                      }`}
                  >
                    <td className="px-4 py-3 font-mono text-purple-300">
                      #{order._id.slice(-8)}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-emerald-300 font-semibold">
                      ${order?.amount?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-lg text-xs font-bold ${order.status === "paid"
                          ? "bg-emerald-300/20 text-emerald-300"
                          : order.status === "failed"
                            ? "bg-red-400/20 text-red-400"
                            : "bg-yellow-300/20 text-yellow-300"
                          }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 relative">
                      <div className="flex flex-col gap-1">
                        {(showAllItemsForOrder === order._id
                          ? order.items
                          : order.items.slice(0, 1)
                        ).map((item, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 bg-white/10 px-2 py-1 rounded text-xs text-gray-200"
                          >
                            <img
                              src={item.game?.cover_image?.url}
                              alt={item.game.title}
                              className="w-5 h-5 object-cover rounded"
                            />
                            {item.game.title}
                          </span>
                        ))}
                        {order.items.length > 1 &&
                          showAllItemsForOrder !== order._id && (
                            <span
                              className="text-xs text-blue-400 underline"
                              onClick={() => setShowAllItemsForOrder(order._id)}
                            >
                              +{order.items.length - 1} more
                            </span>
                          )}
                        {order.items.length > 1 &&
                          showAllItemsForOrder === order._id && (
                            <span
                              className="text-xs text-blue-400 underline"
                              onClick={() => setShowAllItemsForOrder(null)}
                            >
                              - show less
                            </span>
                          )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 font-mono">
                      {/* Razorpay Order ID */}
                    {order.razorpayOrderId ? order.razorpayOrderId :order.stripePaymentIntentId}
                    </td>
                    <td className="px-4 py-3">
                      {order.status === "failed" ? (
                        <button
                          className="px-3 py-1 bg-gradient-primary text-white rounded text-xs"
                          onClick={() => handleRetryPayment(order)}
                          disabled={processingOrderId === order._id}
                        > 
                          {processingOrderId === order._id ? "Processing.." : "Retry"}
                        </button>
                      ) : null}
                      {(order.status === "pending" || order.status === "created") ? (
                        <button
                          className="px-3 py-1 bg-gradient-primary text-white rounded text-xs"
                          onClick={() => handleRetryPayment(order)}
                          disabled={processingOrderId === order._id}
                        >
                          {processingOrderId === order._id ? "Processing.." : "Pay Now"}
                        </button>
                      ) : null}
                      {order.status === "paid" && (
                        <div className="flex flex-col gap-2">
                          <button
                            className="flex items-center justify-center gap-2 px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded text-xs transition-colors w-full"
                            onClick={() => handleViewInvoice(order)}
                            title="View Invoice"
                          >
                            <FaDownload size={10} /> Invoice
                          </button>
                          <button
                            className="flex items-center justify-center gap-2 px-3 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded text-xs transition-colors w-full"
                            onClick={() => navigate('/profile/downloads')}
                            title="Go to Downloads"
                          >
                            <FaDownload size={10} /> Download
                          </button>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      {invoiceModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-none">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-3xl w-full max-w-4xl h-[90vh] flex flex-col shadow-[0_0_50px_rgba(124,58,237,0.1)] relative overflow-hidden font-sans">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/5 bg-[#141414]">
              <h3 className="text-xl font-bold text-white flex items-center gap-3 tracking-wide">
                <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full"></div>
                INVOICE
              </h3>
              <button
                onClick={closeInvoiceModal}
                className="text-white/40 hover:text-white transition-all transform hover:rotate-90 cursor-none"
              >
                <IoIosCloseCircleOutline size={32} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 bg-[#0f0f0f] p-6 sm:p-10 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {invoiceModal.order && (() => {
                const order = invoiceModal.order;
                const userDetails = {
                  name: user?.fullName ? decryptData(user.fullName) : "Customer",
                  email: user?.email ? decryptData(user.email) : "",
                };

                let subtotal = 0;
                if (order.items && Array.isArray(order.items)) {
                  subtotal = order.items.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
                }
                const tax = subtotal * 0.18;
                const discount = subtotal * 0.05;

                return (
                  <div className="relative max-w-3xl mx-auto min-h-[700px] bg-[#111] rounded-2xl overflow-hidden border border-white/5 shadow-2xl p-8 sm:p-12 cursor-none">

                    {/* Background Elements */}
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-pink-600/5 rounded-full blur-[80px] pointer-events-none translate-y-1/3 -translate-x-1/3"></div>

                    {/* Watermark */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                      <h1 className="text-[12rem] font-black text-white/5 -rotate-45 select-none transform origin-center tracking-widest">
                        YOYO
                      </h1>
                    </div>

                    {/* Content Re-Design */}
                    <div className="relative z-10 flex flex-col h-full">

                      {/* Top Bar: Brand & Status */}
                      <div className="flex justify-between items-start mb-12">
                        <div className="space-y-1">
                          <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-1 filter drop-shadow-lg">YOYO GAMES</h1>
                          <p className="text-gray-400 text-sm tracking-wider uppercase">Powered by VS Infotech.</p>
                        </div>
                        <div className="text-right">
                          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${order.status === 'paid' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'} backdrop-blur-sm`}>
                            <div className={`w-2 h-2 rounded-full ${order.status === 'paid' ? 'bg-emerald-400 animate-pulse' : 'bg-yellow-400'}`}></div>
                            <span className="text-xs font-bold uppercase tracking-wider">{order.status}</span>
                          </div>
                        </div>
                      </div>

                      {/* Invoice Information Grid */}
                      <div className="grid grid-cols-2 gap-12 mb-12">
                        {/* Bill To */}
                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span className="w-1 h-1 bg-purple-500 rounded-full"></span> Billed To
                          </h4>
                          <div className="space-y-1">
                            <h2 className="text-xl font-bold text-white">{userDetails.name}</h2>
                            <p className="text-sm text-gray-400 font-medium">{userDetails.email}</p>
                          </div>
                        </div>

                        {/* Details */}
                        <div className="text-right">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center justify-end gap-2">
                            Invoice Details <span className="w-1 h-1 bg-pink-500 rounded-full"></span>
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-end gap-4">
                              <span className="text-sm text-gray-500">Invoice No.</span>
                              <span className="text-sm font-mono font-bold text-white tracking-wider">#{order._id.slice(-8).toUpperCase()}</span>
                            </div>
                            <div className="flex justify-end gap-4">
                              <span className="text-sm text-gray-500">Date Issued</span>
                              <span className="text-sm font-medium text-white">{new Date(order.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decorative Divider */}
                      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8"></div>

                      {/* Items List */}
                      <div className="mb-8 flex-1">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Purchased Items</h4>
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="group relative flex items-center justify-between p-4 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl transition-all duration-300">
                              {/* Hover Glow */}
                              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none"></div>

                              <div className="flex items-center gap-4 relative z-10">
                                <div className="w-12 h-12 rounded-lg bg-[#222] border border-white/10 overflow-hidden shadow-lg group-hover:shadow-purple-500/20 transition-all">
                                  {item.game?.cover_image?.url ? (
                                    <img src={item.game?.cover_image?.url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-600">N/A</div>
                                  )}
                                </div>
                                <div>
                                  <h3 className="font-bold text-white text-sm group-hover:text-purple-300 transition-colors">{item.game?.title || "Unknown Game"}</h3>
                                  <p className="text-xs text-gray-500 capitalize">{item.platform}</p>
                                </div>
                              </div>

                              <div className="relative z-10 text-right">
                                <span className="block font-mono font-medium text-white">${Number(item.price).toFixed(2)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Totals Section */}
                      <div className="flex justify-end">
                        <div className="w-full max-w-sm rounded-2xl bg-white/[0.03] border border-white/10 p-6 relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>

                          <div className="relative z-10 space-y-3">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Subtotal</span>
                              <span className="text-gray-300 font-medium font-mono">${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Tax <span className="text-[10px] bg-white/10 px-1 rounded ml-1">18%</span></span>
                              <span className="text-gray-300 font-medium font-mono">+${tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-500">Discount <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1 rounded ml-1">5%</span></span>
                              <span className="text-emerald-400 font-medium font-mono">-${discount.toFixed(2)}</span>
                            </div>

                            <div className="h-px bg-white/10 my-3"></div>

                            <div className="flex justify-between items-center">
                              <span className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Total </span>
                              <div className="text-right">
                                <span className="block text-2xl font-black text-white tracking-tight">${order?.amount?.toFixed(2)}</span>
                                <span className="block text-[10px] text-gray-500 mt-1">Include of all taxes</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom Footer */}
                      <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                        <p>Thank you for shopping with YOYO!</p>
                        <p className="font-mono">Generated: {new Date().toLocaleTimeString()} • {new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-white/5 bg-[#141414] flex justify-end gap-4">
              <div className="flex gap-3">
                <button
                  onClick={closeInvoiceModal}
                  className="px-6 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all text-sm font-medium border border-white/5 hover:border-white/10 cursor-none"
                >
                  Close
                </button>
                <button
                  onClick={downloadFromModal}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all flex items-center gap-2 text-sm transform hover:-translate-y-0.5 cursor-none"
                >
                  <FaDownload className="text-sm" /> <span>Download Invoice</span>
                </button>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* Stripe Payment Modal */}
      <Modal
        open={openStripeModal}
        onClose={() => setOpenStripeModal(false)}
        aria-labelledby="stripe-checkout-modal"
        aria-describedby="stripe-payment-form"
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'rgba(15, 15, 35, 0.98)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(124, 58, 237, 0.2)',
            padding: '1.5rem',
          }}
        >
          <div className="text-white">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Complete Your Payment
            </h2>
            {clientSecret && orderData && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCheckoutForm
                  clientSecret={clientSecret}
                  orderData={orderData}
                  buyNow={true}
                  onSuccess={() => {
                    setOpenStripeModal(false);
                    setShowSuccessModal(true);
                  }}
                  onCancel={() => setOpenStripeModal(false)}
                />
              </Elements>
            )}
          </div>
        </div>
      </Modal>

      <OrderSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </>
  );
};

export default Orders;
